import { createElement } from 'react';
import { z } from 'zod';

import {
    type DynamicWidgetAdapter,
    type DynamicWidgetBuildContext,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    buildBindingPresentation,
    buildDynamicWidgetId,
    type CaregiverListWidgetModel,
    caregiverListWidgetModelSchema,
    type CaregiverListWidgetRow,
    dynamicWidgetEventSourceSchema,
    type DynamicWidgetIssue,
    DynamicWidgetModelBuilder,
    dynamicWidgetResourceSourceSchema,
    getDynamicWidgetSourceId,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { RetrieveArtifactController } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_artifact_controller';
import { CaregiverListWidget } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_caregiver_list_widget';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

const WORKSPACE_FILE_API_PATH =
    '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}/workspace-files/{file_path}';

// get_recs returns the same workspace-file receipt shape as get_shifts:
// the scored rows live in the JSONL artifact, not the tool output.
const receiptSchema = z.object({
    filename: z.string().min(1),
    row_count: z.number().int().nonnegative(),
    byte_size: z.number().int().nonnegative(),
});

const recommendationRowSchema = z.object({
    caregiver_id: z.string().min(1),
    caregiver_name: z.string().min(1),
    rank: z.number().int(),
    shift_id: z.string().nullish(),
    distance_miles: z.number().nullish(),
    weekly_hours: z.number().nullish(),
    desired_weekly_hours: z.number().nullish(),
    previous_visits: z.number().int().nullish(),
    schedule_availability: z.string().nullish(),
    is_preferred: z.boolean().nullish(),
    roles: z.array(z.string()).nullish(),
    city: z.string().nullish(),
    quality_warnings: z.array(z.string()).nullish(),
});

function normalizeRecsWorkspacePath(filename: string): string | null {
    const trimmed = filename.trim();
    const prefixes = ['/workspace/tmp/recs/', '/tmp/recs/', 'tmp/recs/'];
    const prefix = prefixes.find((candidate) => trimmed.startsWith(candidate));
    if (prefix === undefined) {
        return null;
    }
    const suffix = trimmed.slice(prefix.length);
    const parts = suffix.split('/');
    if (
        suffix.length === 0 ||
        parts.some(
            (part) =>
                part.length === 0 ||
                part === '.' ||
                part === '..' ||
                !/^[A-Za-z0-9._-]+$/.test(part),
        )
    ) {
        return null;
    }
    return `tmp/recs/${suffix}`;
}

function mapRecommendationRow(rawRow: unknown): CaregiverListWidgetRow | null {
    const parsed = recommendationRowSchema.safeParse(rawRow);
    if (!parsed.success) {
        return null;
    }
    const row = parsed.data;
    return {
        caregiverId: row.caregiver_id,
        displayName: row.caregiver_name,
        active: null,
        roles: row.roles ?? [],
        office: null,
        city: row.city ?? null,
        qualificationTags: [],
        valid: true,
        rank: row.rank,
        shiftId: row.shift_id ?? null,
        distanceMiles: row.distance_miles ?? null,
        weeklyHours: row.weekly_hours ?? null,
        desiredWeeklyHours: row.desired_weekly_hours ?? null,
        previousVisits: row.previous_visits ?? null,
        available: row.schedule_availability === 'available',
        preferred: row.is_preferred === true,
        warnings: row.quality_warnings ?? [],
    };
}

export function hydrateCaregiverMatchesModel(
    model: CaregiverListWidgetModel,
    rawRows: ReadonlyArray<unknown>,
): CaregiverListWidgetModel {
    const resourceSource = model.sources.find(
        (candidate) => candidate.kind === 'resource_source',
    );
    const sourceId =
        resourceSource === undefined
            ? null
            : getDynamicWidgetSourceId(resourceSource);
    const rows: Array<CaregiverListWidgetRow> = [];
    const rowIssues: Array<DynamicWidgetIssue> = [];
    rawRows.forEach((rawRow, rowIndex) => {
        const row = mapRecommendationRow(rawRow);
        if (row !== null) {
            rows.push(row);
            return;
        }
        rowIssues.push({
            id: `${model.widgetId}.match-row-${rowIndex}`,
            code: 'source_invalid',
            level: 'warning',
            scope: 'section',
            bindingIds: ['resourceRows'],
            sourceIds: sourceId === null ? [] : [sourceId],
            retryable: false,
            safeDetails: { rowIndex },
        });
    });
    rows.sort((left, right) => (left.rank ?? 0) - (right.rank ?? 0));
    return caregiverListWidgetModelSchema.parse({
        ...model,
        rows,
        rowProvenance: 'resource',
        issues: [
            ...model.issues.filter(
                (issue) => !issue.id.startsWith(`${model.widgetId}.match-row-`),
            ),
            ...rowIssues,
        ],
    });
}

export function buildCaregiverMatchesWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): CaregiverListWidgetModel | null {
    if (
        entry.name !== 'get_recs' ||
        entry.callId === null ||
        !entry.toolCallEventId ||
        !entry.runId
    ) {
        return null;
    }
    const receipt = receiptSchema.parse(entry.output);
    const workspacePath = normalizeRecsWorkspacePath(receipt.filename);
    if (workspacePath === null) {
        return null;
    }
    const eventSource = dynamicWidgetEventSourceSchema.parse({
        kind: 'event_source',
        toolName: entry.name,
        callId: entry.callId,
        toolCallEventId: entry.toolCallEventId,
        toolOutputEventId: entry.id,
        phase: 'output',
        capturedAt: entry.createdAt,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const resourceSource = dynamicWidgetResourceSourceSchema.parse({
        kind: 'resource_source',
        resourceType: 'agent_run_workspace_file',
        resourceId: `${entry.runId}:${workspacePath}`,
        apiPath: WORKSPACE_FILE_API_PATH,
        fetchedAt: null,
        organizationId: context.organizationId,
        mode: context.mode,
    });
    const eventSourceId = getDynamicWidgetSourceId(eventSource);
    const resourceSourceId = getDynamicWidgetSourceId(resourceSource);
    const builder = new DynamicWidgetModelBuilder([
        eventSource,
        resourceSource,
    ]);
    builder.addSourceField({
        id: 'rowCount',
        sourceId: eventSourceId,
        path: '/row_count',
        rawValue: receipt.row_count,
        value: receipt.row_count,
        renderedValue: String(receipt.row_count),
        presentation: buildBindingPresentation({
            section: 'card',
            label: 'rowCount',
        }),
    });
    builder.addSourceField({
        id: 'workspaceFile.path',
        sourceId: eventSourceId,
        path: '/filename',
        rawValue: receipt.filename,
        value: workspacePath,
        renderedValue: workspacePath,
        presentation: buildBindingPresentation({
            section: 'workspaceFile',
            label: 'path',
        }),
    });
    builder.addLiveField({
        id: 'resourceRows',
        sourceIds: [resourceSourceId],
        paths: ['/rows'],
        presentation: buildBindingPresentation({
            section: 'rows',
            label: 'resourceRows',
        }),
    });
    return caregiverListWidgetModelSchema.parse({
        ...builder.buildBase({
            widgetId: buildDynamicWidgetId(entry.toolCallEventId, 'entities'),
            templateVersion: 1,
            issues: [],
        }),
        template: 'entities',
        templateVersion: 1,
        variant: 'ranked',
        rows: [],
        rowCount: receipt.row_count,
        rowProvenance: 'event_preview',
        workspaceFile: {
            runId: entry.runId,
            path: workspacePath,
        },
    });
}

export const caregiverMatchesAdapter: DynamicWidgetAdapter<CaregiverListWidgetModel> =
    {
        template: 'entities',
        templateVersion: 1,
        toolNames: new Set(['get_recs']),
        build: buildCaregiverMatchesWidgetModel,
        render(model, handlers) {
            return createElement(
                RetrieveArtifactController<CaregiverListWidgetModel>,
                {
                    model,
                    hydrateRows: hydrateCaregiverMatchesModel,
                    render: (view) =>
                        createElement(CaregiverListWidget, {
                            model: view.model,
                            loadError: view.loadError,
                            onCaregiverClick: handlers.onCaregiverClick,
                        }),
                },
            );
        },
    };
